<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ExpandableTextArea from 'dashboard/components-next/textarea/ExpandableTextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BotFlowModelsAPI from 'dashboard/api/botFlowModels';
import { usePlanLimits } from 'dashboard/composables/usePlanLimits';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
  onUpdate: {
    type: Function,
    default: null,
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const TABS = {
  PROFILE: 'profile',
  CONFIG: 'config',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

// Tab state
const activeTab = ref(TABS.PROFILE);

// Fallback action options (now stored as JSONB with boolean values)
const FALLBACK_ACTIONS = {
  MARK_CONVERSATION: 'mark_conversation',
  CALL_AGENT: 'call_agent',
  TRY_ANSWER: 'try_answer',
};

// Default fallback state
const DEFAULT_FALLBACK_STATE = {
  mark_conversation: true,
  call_agent: false,
  try_answer: '', // Empty string means disabled, otherwise contains the action type
};

// Try answer action options
const TRY_ANSWER_ACTIONS = {
  TRY_ANYWAY: 'try_anyway',
  STOP_SERVICE: 'stop_service',
  NEXT_STAGE: 'next_stage',
  IGNORE_QUESTION: 'ignore_question',
};

// Bot flow models state
const botFlowModels = ref([]);
const isLoadingModels = ref(false);

const formState = reactive({
  botName: '',
  botProfile: '',
  botRestrictions: '',
  botAvatar: null,
  botAvatarUrl: '',
  selectedInboxId: null,
  botFlowModelId: null,
  fallbackAction: { ...DEFAULT_FALLBACK_STATE },
  fallbackAgentPhone: '',
});

// Check if this is create mode
const isCreateMode = computed(() => props.type === MODAL_TYPES.CREATE);

// Check if call agent is selected (now checking the checkbox state)
const isCallAgentSelected = computed(
  () => formState.fallbackAction?.call_agent === true
);

// Check if try answer is enabled (has a non-empty action value)
const isTryAnswerEnabled = computed(
  () => !!formState.fallbackAction?.try_answer
);

// Toggle try answer checkbox
const toggleTryAnswer = () => {
  if (formState.fallbackAction.try_answer) {
    formState.fallbackAction.try_answer = '';
  } else {
    formState.fallbackAction.try_answer = TRY_ANSWER_ACTIONS.TRY_ANYWAY;
  }
};

// Get selected model details for description display
const selectedModel = computed(() => {
  if (!formState.botFlowModelId) return null;
  return botFlowModels.value.find(m => m.id === formState.botFlowModelId);
});

// Validation rules - name is always required, inbox only on create, phone when call_agent
const validationRules = computed(() => {
  const rules = {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
  };

  // Only require inbox on create
  if (isCreateMode.value) {
    rules.selectedInboxId = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.INBOX'),
        required
      ),
    };
  }

  // Require phone when call_agent is selected
  if (isCallAgentSelected.value) {
    rules.fallbackAgentPhone = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.FALLBACK_PHONE'),
        required
      ),
    };
  }

  return rules;
});

const v$ = useVuelidate(validationRules, formState);

// Fetch inboxes and bot flow models on mount
onMounted(async () => {
  await store.dispatch('inboxes/get');
  await fetchBotFlowModels();
});

// Fetch bot flow models from API
const fetchBotFlowModels = async () => {
  isLoadingModels.value = true;
  try {
    const response = await BotFlowModelsAPI.get();
    botFlowModels.value = response.data || [];

    // If no model is selected and this is create mode, select the default
    if (!formState.botFlowModelId && props.type === MODAL_TYPES.CREATE) {
      const defaultModel = botFlowModels.value.find(m => m.is_default);
      if (defaultModel) {
        formState.botFlowModelId = defaultModel.id;
      }
    }
  } catch (error) {
    console.error('Failed to fetch bot flow models:', error);
  } finally {
    isLoadingModels.value = false;
  }
};

const localLoading = ref(false);

const isLoading = computed(() => {
  if (props.type === MODAL_TYPES.EDIT && props.onUpdate) return localLoading.value;
  return props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating;
});

const dialogTitle = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE')
);

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const inboxError = computed(() =>
  v$.value.selectedInboxId?.$error
    ? v$.value.selectedInboxId.$errors[0]?.$message
    : ''
);

const fallbackPhoneError = computed(() =>
  v$.value.fallbackAgentPhone?.$error
    ? v$.value.fallbackAgentPhone.$errors[0]?.$message
    : ''
);

const resetForm = () => {
  // Find default model to pre-select
  const defaultModel = botFlowModels.value.find(m => m.is_default);

  Object.assign(formState, {
    botName: '',
    botProfile: '',
    botRestrictions: '',
    botAvatar: null,
    botAvatarUrl: '',
    selectedInboxId: null,
    botFlowModelId: defaultModel?.id || null,
    fallbackAction: { ...DEFAULT_FALLBACK_STATE },
    fallbackAgentPhone: '',
  });
  activeTab.value = TABS.PROFILE;
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const botData = {
    name: formState.botName,
    bot_profile: formState.botProfile,
    restrictions: formState.botRestrictions,
    bot_type: 'webhook',
    avatar: formState.botAvatar,
    bot_flow_model_id: formState.botFlowModelId,
    fallback_action: { ...formState.fallbackAction },
    fallback_agent_phone: isCallAgentSelected.value
      ? formState.fallbackAgentPhone
      : null,
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    let result;

    if (!isCreate && props.onUpdate) {
      localLoading.value = true;
      try {
        result = await props.onUpdate(props.selectedBot.id, botData);
      } finally {
        localLoading.value = false;
      }
    } else {
      const actionPayload = isCreate
        ? botData
        : { id: props.selectedBot.id, data: botData };

      result = await store.dispatch(
        `agentBots/${isCreate ? 'create' : 'update'}`,
        actionPayload
      );
    }

    // If creating a new bot, link it to the selected inbox
    if (isCreate && result && formState.selectedInboxId) {
      try {
        await store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: formState.selectedInboxId,
          botId: result.id,
        });
      } catch (linkError) {
        // Bot was created but linking failed
        useAlert(t('AGENT_BOTS.ADD.API.LINK_ERROR'));
        console.error('Failed to link bot to inbox:', linkError);
      }
    }

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    emit('saved', result);
    resetForm();
    dialogRef.value.close();
  } catch (error) {
    // Check if it's a limit exceeded error and show modal
    if (handleApiError(error)) {
      dialogRef.value.close();
      return;
    }

    // Extract error message from backend response if available
    const backendError = error?.response?.data?.error;
    const fallbackKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(backendError || fallbackKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      thumbnail,
      bot_profile: botProfile,
      restrictions,
      bot_flow_model_id: botFlowModelId,
      fallback_action: fallbackAction,
      fallback_agent_phone: fallbackAgentPhone,
    } = props.selectedBot;
    formState.botName = name || '';
    formState.botProfile = botProfile || '';
    formState.botRestrictions = restrictions || '';
    formState.botAvatarUrl = thumbnail || '';
    formState.botFlowModelId = botFlowModelId || null;
    // Handle both old string format and new JSONB format
    if (typeof fallbackAction === 'object' && fallbackAction !== null) {
      formState.fallbackAction = { ...DEFAULT_FALLBACK_STATE, ...fallbackAction };
      // Convert legacy boolean try_answer to new string format
      if (fallbackAction.try_answer === true) {
        formState.fallbackAction.try_answer = 'try_anyway';
      }
    } else {
      // Legacy: convert string to JSONB format
      formState.fallbackAction = {
        mark_conversation: fallbackAction === 'mark_conversation',
        call_agent: fallbackAction === 'call_agent',
        try_answer: fallbackAction === 'try_answer' ? 'try_anyway' : '',
      };
      // If nothing was set, default to mark_conversation
      if (
        !formState.fallbackAction.mark_conversation &&
        !formState.fallbackAction.call_agent &&
        !formState.fallbackAction.try_answer
      ) {
        formState.fallbackAction.mark_conversation = true;
      }
    }
    formState.fallbackAgentPhone = fallbackAgentPhone || '';
  } else {
    resetForm();
  }
};

const closeModal = () => {
  v$.value?.$reset();
  activeTab.value = TABS.PROFILE;
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

const emit = defineEmits(['saved']);
const { handleApiError } = usePlanLimits();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="closeModal"
  >
    <form class="flex flex-col gap-4 max-h-[70vh] min-h-0" @submit.prevent="handleSubmit">
      <!-- Tabs Navigation -->
      <div class="flex border-b border-n-weak flex-shrink-0">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="
            activeTab === 'profile'
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'profile'"
        >
          <span class="flex items-center gap-2">
            <span class="i-lucide-user size-4" />
            {{ $t('AGENT_BOTS.TABS.PROFILE') }}
          </span>
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="
            activeTab === 'config'
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'config'"
        >
          <span class="flex items-center gap-2">
            <span class="i-lucide-settings size-4" />
            {{ $t('AGENT_BOTS.TABS.CONFIG') }}
          </span>
        </button>
      </div>

      <!-- Profile Tab -->
      <div v-show="activeTab === 'profile'" class="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 pr-1">
        <div class="mb-2 flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <Input
          id="bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
          @blur="v$.botName.$touch()"
        />

        <!-- Inbox dropdown - only shown on create -->
        <div v-if="isCreateMode" class="flex flex-col gap-1">
          <label
            for="inbox-select"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ $t('AGENT_BOTS.FORM.INBOX.LABEL') }} *
          </label>
          <select
            id="inbox-select"
            v-model="formState.selectedInboxId"
            class="w-full px-3 py-2 border rounded-lg text-sm bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:border-n-brand focus:outline-none"
            :class="{ 'border-n-ruby-9': inboxError }"
            @blur="v$.selectedInboxId?.$touch()"
          >
            <option :value="null" disabled>
              {{ $t('AGENT_BOTS.FORM.INBOX.PLACEHOLDER') }}
            </option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <span v-if="inboxError" class="text-xs text-n-ruby-9">
            {{ inboxError }}
          </span>
          <span v-else class="text-xs text-n-slate-10">
            {{ $t('AGENT_BOTS.FORM.INBOX.HELP') }}
          </span>
        </div>

        <ExpandableTextArea
          id="bot-profile"
          v-model="formState.botProfile"
          :label="$t('AGENT_BOTS.FORM.BOT_PROFILE.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.BOT_PROFILE.PLACEHOLDER')"
          :rows="6"
        />

        <ExpandableTextArea
          id="bot-restrictions"
          v-model="formState.botRestrictions"
          :label="$t('AGENT_BOTS.FORM.RESTRICTIONS.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.RESTRICTIONS.PLACEHOLDER')"
          :rows="4"
        />
      </div>

      <!-- Config Tab -->
      <div v-show="activeTab === 'config'" class="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0 pr-1">
        <!-- Bot Flow Model Selection -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.FLOW_MODEL.LABEL') }}
          </label>

          <div
            v-if="botFlowModels.length === 0"
            class="p-4 rounded-lg bg-n-alpha-black2 border border-n-weak"
          >
            <p class="text-sm text-n-slate-11">
              {{ $t('AGENT_BOTS.FORM.FLOW_MODEL.NO_MODELS') }}
            </p>
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="model in botFlowModels"
              :key="model.id"
              class="relative flex items-start p-3 rounded-lg border cursor-pointer transition-all"
              :class="
                formState.botFlowModelId === model.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-8'
              "
              @click="formState.botFlowModelId = model.id"
            >
              <div class="flex items-center h-5">
                <input
                  :id="`model-${model.id}`"
                  v-model="formState.botFlowModelId"
                  type="radio"
                  :value="model.id"
                  class="size-4 text-n-brand border-n-weak focus:ring-n-brand"
                />
              </div>
              <div class="ml-3 flex-1">
                <label
                  :for="`model-${model.id}`"
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
                >
                  {{ model.name }}
                  <span
                    v-if="model.is_default"
                    class="px-2 py-0.5 text-xs rounded-full bg-n-brand/20 text-n-brand"
                  >
                    {{ $t('AGENT_BOTS.FORM.FLOW_MODEL.DEFAULT') }}
                  </span>
                </label>
                <p
                  v-if="model.description"
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{ model.description }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <hr class="border-n-weak" />

        <!-- Fallback Action Section -->
        <div class="flex flex-col gap-4">
          <h4 class="text-sm font-medium text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-life-buoy size-4" />
            {{ $t('AGENT_BOTS.FORM.FALLBACK_SECTION.TITLE') }}
          </h4>

          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12 mb-2">
              {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.LABEL') }}
            </span>

            <!-- Checkbox: Mark Conversation -->
            <label
              class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
              :class="
                formState.fallbackAction.mark_conversation
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-8'
              "
            >
              <input
                v-model="formState.fallbackAction.mark_conversation"
                type="checkbox"
                class="size-4 text-n-brand border-n-weak rounded focus:ring-n-brand"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.OPTIONS.MARK_CONVERSATION') }}
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.DESCRIPTIONS.MARK_CONVERSATION') }}
                </p>
              </div>
            </label>

            <!-- Checkbox: Call Agent -->
            <label
              class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
              :class="
                formState.fallbackAction.call_agent
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-alpha-black2 hover:border-n-slate-8'
              "
            >
              <input
                v-model="formState.fallbackAction.call_agent"
                type="checkbox"
                class="size-4 text-n-brand border-n-weak rounded focus:ring-n-brand"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.OPTIONS.CALL_AGENT') }}
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.DESCRIPTIONS.CALL_AGENT') }}
                </p>
              </div>
            </label>

            <!-- Checkbox: Try Answer with Dropdown -->
            <div
              class="p-3 rounded-lg border transition-all"
              :class="
                isTryAnswerEnabled
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-alpha-black2'
              "
            >
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  :checked="isTryAnswerEnabled"
                  type="checkbox"
                  class="size-4 text-n-brand border-n-weak rounded focus:ring-n-brand"
                  @change="toggleTryAnswer"
                />
                <div class="flex-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.OPTIONS.TRY_ANSWER') }}
                  </span>
                  <p class="text-xs text-n-slate-10 mt-0.5">
                    {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.DESCRIPTIONS.TRY_ANSWER') }}
                  </p>
                </div>
              </label>

              <!-- Dropdown for try answer action - shown when enabled -->
              <div v-if="isTryAnswerEnabled" class="mt-3 ml-7">
                <label
                  for="try-answer-action"
                  class="text-xs font-medium text-n-slate-11 mb-1 block"
                >
                  {{ $t('AGENT_BOTS.FORM.TRY_ANSWER_ACTION.LABEL') }}
                </label>
                <select
                  id="try-answer-action"
                  v-model="formState.fallbackAction.try_answer"
                  class="w-full px-3 py-2 border rounded-lg text-sm bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:border-n-brand focus:outline-none"
                >
                  <option :value="'try_anyway'">
                    {{ $t('AGENT_BOTS.FORM.TRY_ANSWER_ACTION.OPTIONS.TRY_ANYWAY') }}
                  </option>
                  <option :value="'stop_service'">
                    {{ $t('AGENT_BOTS.FORM.TRY_ANSWER_ACTION.OPTIONS.STOP_SERVICE') }}
                  </option>
                  <option :value="'next_stage'">
                    {{ $t('AGENT_BOTS.FORM.TRY_ANSWER_ACTION.OPTIONS.NEXT_STAGE') }}
                  </option>
                  <option :value="'ignore_question'">
                    {{ $t('AGENT_BOTS.FORM.TRY_ANSWER_ACTION.OPTIONS.IGNORE_QUESTION') }}
                  </option>
                </select>
              </div>
            </div>

            <span class="text-xs text-n-slate-10 mt-1">
              {{ $t('AGENT_BOTS.FORM.FALLBACK_ACTION.HELP') }}
            </span>
          </div>

          <!-- Agent Phone - only shown when call_agent is selected -->
          <div v-if="isCallAgentSelected" class="flex flex-col gap-1">
            <Input
              id="fallback-agent-phone"
              v-model="formState.fallbackAgentPhone"
              :label="$t('AGENT_BOTS.FORM.FALLBACK_PHONE.LABEL')"
              :placeholder="$t('AGENT_BOTS.FORM.FALLBACK_PHONE.PLACEHOLDER')"
              :message="fallbackPhoneError"
              :message-type="fallbackPhoneError ? 'error' : 'info'"
              @blur="v$.fallbackAgentPhone?.$touch()"
            />
            <span v-if="!fallbackPhoneError" class="text-xs text-n-slate-10">
              {{ $t('AGENT_BOTS.FORM.FALLBACK_PHONE.HELP') }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2 border-t border-n-weak pt-4 flex-shrink-0">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
